import React, { useEffect, useState, useRef } from 'react';
import { useNavigate }                        from 'react-router-dom';
import { useAuth }                            from '../contexts/AuthContext';
import {
  adminListUsers, adminLockUser, adminUnlockUser,
  adminDeactivateUser, adminGetAiUsage, adminLoadVehicleConfig,
} from '../api/admin';
import './AdminPage.css';

const TABS = [
  { key: 'users',   icon: '👥', label: 'Users'          },
  { key: 'ai',      icon: '🤖', label: 'AI Usage'        },
  { key: 'config',  icon: '🚗', label: 'Vehicle Config'  },
];

export default function AdminPage() {
  const { user, signOut }     = useAuth();
  const navigate              = useNavigate();
  const [activeTab, setTab]   = useState('users');

  function handleSignOut() {
    signOut();
    navigate('/');
  }

  return (
    <div className="adm-root">

      {/* ── Header ────────────────────────────────────────────────── */}
      <header className="adm-header">
        <div className="adm-header-brand">
          <span className="adm-header-gear">⚙</span>
          <span className="adm-header-name">AutoRestoration</span>
          <span className="adm-admin-badge">ADMIN</span>
        </div>
        <div className="adm-header-user">
          <button className="adm-back-btn" onClick={() => navigate('/dashboard')}>
            ← Dashboard
          </button>
          <span className="adm-username">@{user?.username}</span>
          <button className="adm-signout-btn" onClick={handleSignOut}>Sign Out</button>
        </div>
      </header>

      <div className="adm-body">

        {/* ── Side tabs ─────────────────────────────────────────── */}
        <nav className="adm-nav">
          <p className="adm-nav-heading">Admin Tools</p>
          {TABS.map(t => (
            <button
              key={t.key}
              className={`adm-nav-item ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              <span className="adm-nav-icon">{t.icon}</span>
              <span className="adm-nav-label">{t.label}</span>
            </button>
          ))}
        </nav>

        {/* ── Main panel ────────────────────────────────────────── */}
        <main className="adm-main">
          {activeTab === 'users'  && <UsersTab  />}
          {activeTab === 'ai'     && <AiUsageTab />}
          {activeTab === 'config' && <VehicleConfigTab />}
        </main>

      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   USERS TAB
══════════════════════════════════════════════════════════════════ */

function UsersTab() {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [working, setWorking] = useState(null); // userAccountId being acted on

  const load = () => {
    setLoading(true);
    adminListUsers()
      .then(r => setUsers(r.success ? r.data : []))
      .catch(() => setError('Failed to load users.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  async function doAction(action, id) {
    setWorking(id);
    try {
      if (action === 'lock')       await adminLockUser(id);
      if (action === 'unlock')     await adminUnlockUser(id);
      if (action === 'deactivate') await adminDeactivateUser(id);
      load();
    } catch { setError('Action failed.'); }
    finally  { setWorking(null); }
  }

  return (
    <div className="adm-panel">
      <div className="adm-panel-header">
        <h2 className="adm-panel-title">👥 Users</h2>
        <span className="adm-panel-count">{users.length} accounts</span>
      </div>

      {error   && <p className="adm-error">{error}</p>}
      {loading ? (
        <p className="adm-loading">Loading users…</p>
      ) : (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Display Name</th>
                <th>Email</th>
                <th>Roles</th>
                <th>Type</th>
                <th>Status</th>
                <th>Last Activity</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.userAccountId} className={!u.is_active ? 'adm-row-inactive' : ''}>
                  <td className="adm-cell-username">@{u.username}</td>
                  <td>{u.display_name || '—'}</td>
                  <td>{u.email}</td>
                  <td>
                    {(u.roles || '').split(',').filter(Boolean).map(r => (
                      <span key={r} className={`adm-role-badge ${r.trim()}`}>{r.trim()}</span>
                    ))}
                  </td>
                  <td>{u.account_type || '—'}</td>
                  <td>
                    <span className={`adm-status-badge ${u.is_active ? (u.is_locked ? 'locked' : 'active') : 'inactive'}`}>
                      {!u.is_active ? 'Inactive' : u.is_locked ? 'Locked' : 'Active'}
                    </span>
                  </td>
                  <td className="adm-cell-date">
                    {u.last_activity ? new Date(u.last_activity).toLocaleString() : '—'}
                  </td>
                  <td className="adm-cell-actions">
                    {u.is_active && !u.is_locked && (
                      <button
                        className="adm-action-btn warn"
                        disabled={working === u.userAccountId}
                        onClick={() => doAction('lock', u.userAccountId)}
                      >Lock</button>
                    )}
                    {u.is_active && u.is_locked && (
                      <button
                        className="adm-action-btn ok"
                        disabled={working === u.userAccountId}
                        onClick={() => doAction('unlock', u.userAccountId)}
                      >Unlock</button>
                    )}
                    {u.is_active && (
                      <button
                        className="adm-action-btn danger"
                        disabled={working === u.userAccountId}
                        onClick={() => {
                          if (window.confirm(`Deactivate @${u.username}?`)) {
                            doAction('deactivate', u.userAccountId);
                          }
                        }}
                      >Deactivate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   AI USAGE TAB
══════════════════════════════════════════════════════════════════ */

function AiUsageTab() {
  const [records,  setRecords]  = useState([]);
  const [summary,  setSummary]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [filters,  setFilters]  = useState({ startDate: '', endDate: '', userAccountId: '' });

  const load = (f = filters) => {
    setLoading(true);
    const params = {};
    if (f.startDate)     params.startDate     = f.startDate;
    if (f.endDate)       params.endDate       = f.endDate;
    if (f.userAccountId) params.userAccountId = f.userAccountId;

    adminGetAiUsage(params)
      .then(r => {
        if (r.success) { setRecords(r.data.records); setSummary(r.data.summary); }
        else setError('Failed to load AI usage.');
      })
      .catch(() => setError('Failed to load AI usage.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  function handleFilter(e) {
    e.preventDefault();
    load(filters);
  }

  return (
    <div className="adm-panel">
      <div className="adm-panel-header">
        <h2 className="adm-panel-title">🤖 AI Usage</h2>
      </div>

      {/* Filter bar */}
      <form className="adm-filter-bar" onSubmit={handleFilter}>
        <div className="adm-filter-group">
          <label className="adm-filter-label">From</label>
          <input type="date" className="adm-filter-input"
            value={filters.startDate}
            onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))} />
        </div>
        <div className="adm-filter-group">
          <label className="adm-filter-label">To</label>
          <input type="date" className="adm-filter-input"
            value={filters.endDate}
            onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))} />
        </div>
        <div className="adm-filter-group">
          <label className="adm-filter-label">User ID</label>
          <input type="number" className="adm-filter-input" placeholder="All users"
            value={filters.userAccountId}
            onChange={e => setFilters(f => ({ ...f, userAccountId: e.target.value }))} />
        </div>
        <button type="submit" className="adm-filter-btn">Filter</button>
        <button type="button" className="adm-filter-btn secondary"
          onClick={() => { const blank = { startDate:'', endDate:'', userAccountId:'' }; setFilters(blank); load(blank); }}>
          Clear
        </button>
      </form>

      {error && <p className="adm-error">{error}</p>}

      {/* Summary cards */}
      {summary && (
        <div className="adm-summary-cards">
          <div className="adm-summary-card">
            <span className="adm-summary-value">{summary.total_calls}</span>
            <span className="adm-summary-label">Total Calls</span>
          </div>
          <div className="adm-summary-card">
            <span className="adm-summary-value">{summary.failed_calls}</span>
            <span className="adm-summary-label">Failed</span>
          </div>
          <div className="adm-summary-card">
            <span className="adm-summary-value">{(summary.total_input_tokens + summary.total_output_tokens).toLocaleString()}</span>
            <span className="adm-summary-label">Total Tokens</span>
          </div>
          <div className="adm-summary-card highlight">
            <span className="adm-summary-value">${summary.total_cost_usd.toFixed(4)}</span>
            <span className="adm-summary-label">Est. Cost (USD)</span>
          </div>
        </div>
      )}

      {loading ? (
        <p className="adm-loading">Loading AI usage…</p>
      ) : (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Action</th>
                <th>Model</th>
                <th>Input Tokens</th>
                <th>Output Tokens</th>
                <th>Duration</th>
                <th>Status</th>
                <th>Est. Cost</th>
                <th>Billed</th>
              </tr>
            </thead>
            <tbody>
              {records.map(r => (
                <tr key={r.processId} className={r.status === 'FAILED' ? 'adm-row-failed' : ''}>
                  <td className="adm-cell-date">
                    {r.started_at ? new Date(r.started_at).toLocaleString() : '—'}
                  </td>
                  <td>{r.username || <span className="adm-muted">system</span>}</td>
                  <td><span className="adm-tag">{r.action_type}</span></td>
                  <td className="adm-cell-model">{r.model}</td>
                  <td>{r.input_tokens?.toLocaleString()  ?? '—'}</td>
                  <td>{r.output_tokens?.toLocaleString() ?? '—'}</td>
                  <td>{r.duration_ms ? `${r.duration_ms}ms` : '—'}</td>
                  <td>
                    <span className={`adm-status-badge ${(r.status||'').toLowerCase()}`}>
                      {r.status || '—'}
                    </span>
                  </td>
                  <td>${parseFloat(r.estimated_cost_usd || 0).toFixed(6)}</td>
                  <td>{r.is_billed ? `✓ ${r.billed_at ? new Date(r.billed_at).toLocaleDateString() : ''}` : '—'}</td>
                </tr>
              ))}
              {records.length === 0 && (
                <tr><td colSpan={10} className="adm-empty-row">No records found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   VEHICLE CONFIG TAB
══════════════════════════════════════════════════════════════════ */

function VehicleConfigTab() {
  const fileRef               = useRef(null);
  const [jsonText, setJson]   = useState('');
  const [result,   setResult] = useState('');
  const [error,    setError]  = useState('');
  const [loading,  setLoad]   = useState(false);
  const [fileName, setFN]     = useState('');

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setFN(file.name);
    setError('');
    setResult('');

    const reader = new FileReader();
    reader.onload = ev => {
      const text = ev.target.result;
      // Try parsing as JSON directly; if it fails, try CSV
      try {
        const parsed = JSON.parse(text);
        setJson(JSON.stringify(parsed, null, 2));
      } catch {
        // Try to convert CSV → JSON
        try {
          const converted = csvToConfig(text);
          setJson(JSON.stringify(converted, null, 2));
        } catch (csvErr) {
          setError('Could not parse file. Please upload a JSON array or CSV with columns: year,make,model,package,engine');
        }
      }
    };
    reader.readAsText(file);
  }

  function csvToConfig(text) {
    const lines = text.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g,'_'));
    return lines.slice(1).filter(l => l.trim()).map(line => {
      const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      const obj = {};
      headers.forEach((h, i) => { obj[h] = vals[i] || null; });
      if (obj.year) obj.year = parseInt(obj.year);
      return obj;
    });
  }

  async function handleUpload() {
    if (!jsonText.trim()) { setError('No data to upload.'); return; }
    setLoad(true);
    setError('');
    setResult('');
    try {
      const parsed = JSON.parse(jsonText);
      const res = await adminLoadVehicleConfig(parsed);
      if (res.success) {
        setResult(`✓ Successfully loaded ${res.data.configs_processed} vehicle configuration(s).`);
        setJson('');
        setFN('');
      } else {
        setError(res.message || 'Upload failed.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed — check JSON format.');
    } finally {
      setLoad(false);
    }
  }

  const EXAMPLE = JSON.stringify([
    { year: 1969, make: "Chevrolet", model: "Camaro", package: "Z/28", engine: "302 V8 DZ" },
    { year: 1970, make: "Chevrolet", model: "Chevelle", package: "SS 454", engine: "454 V8 LS6" }
  ], null, 2);

  return (
    <div className="adm-panel">
      <div className="adm-panel-header">
        <h2 className="adm-panel-title">🚗 Vehicle Config Upload</h2>
      </div>

      <p className="adm-panel-desc">
        Upload a JSON or CSV file containing vehicle year/make/model/package/engine combinations.
        These will be added to the reference catalog used by the cascading dropdowns when users add a project car.
      </p>

      {/* Format reference */}
      <div className="adm-config-format">
        <p className="adm-config-format-label">Expected JSON format:</p>
        <pre className="adm-config-pre">{EXAMPLE}</pre>
        <p className="adm-config-format-label" style={{ marginTop: '10px' }}>
          CSV format — first row must be headers: <code>year,make,model,package,engine</code>
        </p>
      </div>

      {/* File picker */}
      <div className="adm-config-upload-row">
        <input
          ref={fileRef}
          type="file"
          accept=".json,.csv,.txt"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <button className="adm-upload-pick-btn" onClick={() => fileRef.current.click()}>
          📂 Choose File
        </button>
        {fileName && <span className="adm-filename">{fileName}</span>}
      </div>

      {/* JSON preview / manual edit */}
      <div className="adm-config-editor-wrap">
        <label className="adm-config-editor-label">
          JSON Preview / Manual Entry
          {jsonText && (
            <span className="adm-config-count">
              {(() => { try { return ` — ${JSON.parse(jsonText).length} records`; } catch { return ''; } })()}
            </span>
          )}
        </label>
        <textarea
          className="adm-config-editor"
          placeholder={`Paste JSON array here, or choose a file above.\n\n${EXAMPLE}`}
          value={jsonText}
          onChange={e => { setJson(e.target.value); setError(''); setResult(''); }}
          rows={14}
        />
      </div>

      {error  && <p className="adm-error">{error}</p>}
      {result && <p className="adm-success">{result}</p>}

      <div className="adm-config-actions">
        <button
          className="adm-upload-btn"
          onClick={handleUpload}
          disabled={loading || !jsonText.trim()}
        >
          {loading ? 'Uploading…' : '⬆ Upload to Catalog'}
        </button>
        <button
          className="adm-clear-btn"
          onClick={() => { setJson(''); setFN(''); setError(''); setResult(''); }}
        >
          Clear
        </button>
      </div>
    </div>
  );
}
